// SCR-STAFF-307 — صف تأیید رزروهای آنلاین (US-SCH-026؛ AC-SCH-026.1؛ C-AGENT-1404): درخواست‌های Requested پرتال و ویجت با مهلت تأیید؛ تأیید ←
// ثبت یا در انتظار بیعانه (درخواست بیعانه پس از تأیید)، رد ← آزادشدن زمان و پیشنهاد به لیست انتظار؛ Permission همان `sch.appointment.update`.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { unwrap, type AppointmentListItem } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const SCR_STAFF_307 = 'SCR-STAFF-307';

export function OnlineRequestsPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('sch.appointment.update');
  const queryClient = useQueryClient();
  const key = ['sch', session?.tenantId, 'online-requests'];
  const requests = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/appointments', { params: { query: { status: 'Requested', limit: 200 } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });
  const approve = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:approve-request', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const reject = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/scheduling/appointments/{id}:reject-request', { params: { path: { id } } })),
    onSuccess: refresh,
  });

  const columns: DataTableColumn<AppointmentListItem>[] = [
    { id: 'patient', headerKey: 'sch:online_requests.patient', cell: (row) => row.patientName ?? '' },
    { id: 'start', headerKey: 'sch:online_requests.start', cell: (row) => <JalaliDate value={row.start} withTime /> },
    { id: 'service', headerKey: 'sch:booking.service', cell: (row) => [row.serviceName, row.providerName].filter(Boolean).join(' — ') },
    { id: 'channel', headerKey: 'sch:online_requests.channel', cell: (row) => (row.channel ? <Chip size="small" label={t(`sch:channel.${row.channel}`)} /> : '') },
    { id: 'expires', headerKey: 'sch:online_requests.expires', cell: (row) => (row.requestExpiresAt ? <JalaliDate value={row.requestExpiresAt} withTime /> : '') },
    {
      id: 'actions',
      headerKey: 'sch:online_requests.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" disabled={!write.canWrite || approve.isPending} onClick={() => approve.mutate(row.id)} data-testid={`sch-request-approve-${row.id}`}>
            {t('sch:online_requests.approve')}
          </Button>
          <Button size="small" color="error" disabled={!write.canWrite || reject.isPending} onClick={() => reject.mutate(row.id)} data-testid={`sch-request-reject-${row.id}`}>
            {t('sch:online_requests.reject')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_307}
      titleKey="sch:online_requests.title"
      filters={
        <Stack spacing={1}>
          <Alert severity="info" variant="outlined">
            {t('sch:online_requests.note')}
          </Alert>
          {approve.isSuccess ? <Alert severity="success">{t('sch:online_requests.approved')}</Alert> : null}
          {reject.isSuccess ? <Alert severity="success">{t('sch:online_requests.rejected')}</Alert> : null}
          {approve.error ? <ErrorState error={approve.error} compact /> : null}
          {reject.error ? <ErrorState error={reject.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        columns={columns}
        rows={requests.data?.items}
        getRowId={(row) => row.id}
        isLoading={requests.isPending}
        error={requests.error}
        onRetry={() => void requests.refetch()}
        emptyTitleKey="sch:online_requests.empty"
        captionKey="sch:online_requests.title"
      />
    </ListPage>
  );
}
